import {Link} from 'react-router-dom';
import {DataTable, type Column} from '@common-web/components/data-table';
import {RiskIndicator} from './RiskIndicator';
import {AssigneeAvatar} from './AssigneeAvatar';
import type {OnboardingItem} from './PipelinePhaseCard';

interface PipelineTableViewProps {
  onboardings: OnboardingItem[];
  loading?: boolean;
}

const phaseConfig: Record<OnboardingItem['currentPhase'], {label: string; badge: string}> = {
  INITIAL_SETUP: {label: 'Initial Setup', badge: 'bg-brand-navy text-white'},
  PRE_ENROLLMENT: {label: 'Pre-Enrollment', badge: 'bg-brand-blue text-white'},
  ENROLLMENT: {label: 'Enrollment', badge: 'bg-brand-blue-light text-white'},
  GO_LIVE_PREP: {label: 'Go Live Prep', badge: 'bg-amber-500 text-white'},
  POST_LAUNCH: {label: 'Post Launch', badge: 'bg-emerald-500 text-white'},
};

const columns: Column<OnboardingItem>[] = [
  {
    id: 'groupName',
    header: 'Group',
    render: (item) => (
      <Link to={`/onboarding/${item.id}`} className='text-brand-blue hover:underline font-medium'>
        {item.groupName}
      </Link>
    ),
  },
  {
    id: 'currentPhase',
    header: 'Phase',
    render: (item) => {
      const cfg = phaseConfig[item.currentPhase];
      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}
        >
          {cfg.label}
        </span>
      );
    },
  },
  {
    id: 'progress',
    header: 'Progress',
    render: (item) => (
      <div className='min-w-[120px]'>
        <div className='h-2 w-full bg-gray-200 rounded-full overflow-hidden'>
          <div
            className='h-full bg-emerald-500 rounded-full transition-all'
            style={{width: `${item.progressPercent}%`}}
          />
        </div>
        <p className='text-xs text-brand-gray mt-1'>
          {item.completedSteps}/{item.totalSteps}
        </p>
      </div>
    ),
  },
  {
    id: 'daysInPipeline',
    header: 'Days',
    render: (item) => <span className='text-sm text-brand-gray'>{item.daysInPipeline}d</span>,
  },
  {
    id: 'riskLevel',
    header: 'Risk',
    render: (item) => <RiskIndicator level={item.riskLevel} />,
  },
  {
    id: 'assignedManager',
    header: 'Manager',
    render: (item) =>
      item.assignedManager ? (
        <AssigneeAvatar name={item.assignedManager.name} size='sm' />
      ) : (
        <span className='text-brand-gray-light text-xs'>Unassigned</span>
      ),
  },
];

export function PipelineTableView({onboardings, loading}: PipelineTableViewProps) {
  return (
    <DataTable<OnboardingItem>
      columns={columns}
      data={onboardings}
      loading={loading}
      emptyMessage='No onboardings found'
    />
  );
}
